import { PWAMiniBadge } from './components/PWAMiniBadge';
import './components/PWAMiniBadge.css';

import { useState, useEffect, Suspense, lazy } from "react";
import { ref, onValue } from "firebase/database";
import { database } from "./firebase.config";
import { SetupScreen } from "./components/SetupScreen";
import { GameController } from "./components/GameController";
import { SoundToggle } from "./components/SoundToggle";
import { AdminRoute } from "./components/admin/AdminRoute";
import { AdminMetricsPage } from "./pages/admin/AdminMetricsPage";
import { AdminLibraryUploadPage } from "./pages/admin/AdminLibraryUploadPage";
import { AdminBulkUploadPage } from "./pages/admin/AdminBulkUploadPage";
import { AdminLibraryFixPage } from "./pages/admin/AdminLibraryFixPage";

// ✅ PÁGINAS
import { HomePage } from "./pages/HomePage";
import { SemaforoPage } from "./pages/SemaforoPage";
import { HerramientasPage } from "./pages/HerramientasPage";
import { AboutPage } from "./pages/AboutPage";
import { LibraryPage } from "./pages/LibraryPage";
import { JoinGamePage } from "./pages/JoinGamePage";
import { ReferralPage } from "./pages/ReferralPage";
import { WebinarRegistrationPage } from "./pages/WebinarRegistrationPage";
import { PrivacyPage } from "./pages/PrivacyPage";

// ✅ NUEVO: Componentes de propuestas
import { SetupFlowManager } from "./components/setup/SetupFlowManager";
import { ProposalStudentView } from "./components/Stage0/ProposalStudentView";

// ✅ NUEVO: Páginas de autoevaluación y resultados
import { SelfEvaluationPage } from "./pages/SelfEvaluationPage";
import { GameResultsPage } from "./pages/GameResultsPage";

// 🔁 NUEVO: Componentes para autoevaluación desde QR y panel docente
import { SelfEvalPage, TeacherEvaluationsPage } from "./components/PostGame";

// ✅ CLASSROOM VIEWS REALES
import { Stage1ClassroomView } from "./components/Stage1/Stage1ClassroomView";
import { ProposalTeacherView } from "./components/Stage0/ProposalTeacherView";
import { ProposalCurationView } from "./components/Stage0/ProposalCurationView";
import {
  closeProposalsAndStartCuration,
  completeCurationAndStartStage1,
} from "./services/stage0Service";
import { ClassroomView as Stage2ClassroomView } from "./components/Stage2/ClassroomView";

// ✅ PWA INSTALL BANNER
import { PWAInstallBanner } from "./components/PWAInstallBanner";

import "./App.css";

// ✅ AUTH + ROUTER + GAME MODE + I18N
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { GameModeProvider } from "./contexts/GameModeContext";
import { I18nProvider, useI18n, htmlLangForPath } from "./i18n";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { LoginPage } from "./pages/LoginPage";
import { setManifestForRoute } from "./utils/dynamicManifest";
import { normalizarCodigoSesion } from "./utils/arbitroCodigo";

import type { Game } from "./types/game";

// 🆕 Página de Guía Pedagógica (Lazy Load)
const TeacherGuide = lazy(() => import('./pages/TeacherGuide'));

// 🆕 Generador de Rúbricas (Lazy Load)
const RubricGenerator = lazy(() =>
  import('./pages/RubricGeneratorPage').then(m => ({ default: m.RubricGeneratorPage }))
);

// ✅ NUEVO: Adaptador de consignas (Lazy Load)
const ConsignaAdapter = lazy(() =>
  import('./pages/ConsignaAdapterPage').then(m => ({ default: m.ConsignaAdapterPage }))
);

// ✅ NUEVO: Generador de Informes (Lazy Load)
const InformeGenerator = lazy(() =>
  import('./pages/InformeGeneratorPage').then(m => ({ default: m.InformeGeneratorPage }))
);

// ✅ NUEVO: Generador de Planilla de Presentismo (Lazy Load)
const PresentismoGenerator = lazy(() =>
  import('./pages/PresentismoGeneratorPage').then(m => ({ default: m.PresentismoGeneratorPage }))
);

// 🆕 GENERADOR DE PRESENTACIONES (LAZY LOAD)
const PresentacionGenerator = lazy(() =>
  import('./pages/PresentacionGeneratorPage').then(m => ({ default: m.PresentacionGeneratorPage }))
);

// 🆕 GENERADOR DE HORARIOS (LAZY LOAD)
const HorarioGenerator = lazy(() =>
  import('./pages/HorarioGeneratorPage').then(m => ({ default: m.HorarioGeneratorPage }))
);

// 🆕 GENERADOR DE SECUENCIAS DIDÁCTICAS (LAZY LOAD)
const SecuenciaGenerator = lazy(() =>
  import('./pages/SecuenciaGeneratorPage').then(m => ({ default: m.SecuenciaGeneratorPage }))
);
// 🆕 CORRECTOR CON RÚBRICA (LAZY LOAD)
const Correccion = lazy(() =>
  import('./pages/CorreccionPage').then(m => ({ default: m.CorreccionPage }))
);

// 🆕 GENERADOR DE PLANIFICACIONES (LAZY LOAD)
const PlanificacionGeneratorPage = lazy(() => import("./pages/PlanificacionGeneratorPage"));

// ✅ NUEVO: Comparador de Países (Lazy Load)
const ComparadorPaisesPage = lazy(() => import("./pages/ComparadorPaisesPage"));

// 🆕 GENERADOR DE ECUACIONES (Lazy Load)
const EcuacionesGeneratorPage = lazy(() => import("./pages/EcuacionesGeneratorPage"));

// 🆕 VIANDAS DE MIRE (Lazy Load)
const ViandasMirePage = lazy(() => import("./pages/ViandasMirePage"));

// 🆕 TABLAS DE MULTIPLICAR — BINGO (Lazy Load)
const TablasBingoPage      = lazy(() => import("./pages/TablasBingoPage"));
const BingoSumasRestasPage = lazy(() => import("./pages/BingoSumasRestasPage"));
const BingoSumasPage       = lazy(() => import("./pages/BingoSumasPage"));
const BingoTablasDosPage   = lazy(() => import("./pages/BingoTablasDosPage"));
const BingoTablasTresPage  = lazy(() => import("./pages/BingoTablasTresPage"));
const BingoFamiliarPage    = lazy(() => import("./pages/BingoFamiliarPage"));

// 🆕 ÁRBITRO DE LENGUA Y MATEMÁTICA (Lazy Load)
const ArbitroConfigPage = lazy(() => import("./pages/ArbitroConfigPage"));
const ArbitroSesionPage = lazy(() => import("./pages/ArbitroSesionPage"));
const ArbitroPanelPage = lazy(() => import("./pages/ArbitroPanelPage"));
const ArbitroLandingPage = lazy(() => import("./pages/ArbitroLandingPage"));
const ArbitrosInfoPage = lazy(() => import("./pages/ArbitrosInfoPage"));
const ArbitrosPublicaPage = lazy(() => import("./pages/ArbitrosPublicaPage"));
const ArbitroJuegoPage = lazy(() => import("./pages/ArbitroJuegoPage"));
const ArbitroIngresoPage = lazy(() => import("./pages/ArbitroIngresoPage"));

// 📊 A/B TEST DASHBOARD
import { ABTestDashboard } from "./components/ABTestDashboard";

// ✅ CORREGIDO: ClassroomView real para Stage 1
function ClassroomRouteWrapper() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gameId) return;

    const gameRef = ref(database, `games/${gameId}`);
    const unsubscribe = onValue(gameRef, (snapshot) => {
      if (snapshot.exists()) {
        setGame(snapshot.val());
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [gameId]);

  if (!gameId) return <div style={{ padding: 20 }}>❌ gameId no encontrado</div>;

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
          <div style={{ fontSize: 18, color: '#64748b' }}>Cargando juego...</div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
        <div style={{ fontSize: 18, color: '#ef4444' }}>Juego no encontrado</div>
        <p style={{ color: '#64748b' }}>gameId: {gameId}</p>
      </div>
    );
  }

  // Detectar Stage 0 y renderizar la vista correcta según la fase
  const currentPhase = game.status?.currentPhase || "";
  const currentStage = game.status?.currentStage ?? 1;

  if (currentStage === 0 || currentPhase.startsWith("proposal")) {
    const teams = game.teams
      ? Object.values(game.teams as any).map((t: any) => ({ ...t, id: t.id }))
      : [];

    if (currentPhase === "proposal-curating") {
      return (
        <ProposalCurationView
          gameId={gameId}
          teams={teams as any}
          onComplete={async (questions, bonusPoints) => {
            await completeCurationAndStartStage1(gameId, questions, bonusPoints);
            navigate(`/classroom/${gameId}`);
          }}
          onBack={() => navigate("/semaforo")}
        />
      );
    }

    // proposal-collecting o proposal-waiting
    return (
      <ProposalTeacherView
        gameId={gameId}
        config={{
          maxProposalsPerTeam: game.config?.stage0Config?.maxProposalsPerTeam ?? 5,
          timerMinutes: game.config?.stage0Config?.timerMinutes ?? null,
          showLiveProposals: game.config?.stage0Config?.showLiveProposals ?? true,
        }}
        onCloseProposals={async () => {
          await closeProposalsAndStartCuration(gameId);
          navigate(`/classroom/${gameId}`);
        }}
      />
    );
  }

  return <Stage1ClassroomView game={game} gameId={gameId} />;
}

// ✅ CORREGIDO: ClassroomView real para Stage 2
function Stage2ClassroomRouteWrapper() {
  const { gameId } = useParams();

  if (!gameId) return <div style={{ padding: 20 }}>❌ gameId no encontrado</div>;

  return <Stage2ClassroomView gameId={gameId} />;
}

// ✅ Rutas de alumnos
function TeamRouteWrapper() {
  const { gameId, teamId } = useParams();
  if (!gameId || !teamId) return <div>Ruta inválida</div>;

  return (
    <div className="App">
      <div style={{ position: "fixed", top: 16, right: 16, zIndex: 1000 }}>
        <SoundToggle />
      </div>
      <GameController gameId={gameId} teamId={teamId} />
    </div>
  );
}

// ✅ NUEVO: Wrapper para vista de propuestas del alumno
function ProposalRouteWrapper() {
  const { gameId, teamId } = useParams();
  const [teamData, setTeamData] = useState<{ name: string; emoji: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gameId || !teamId) return;

    const teamRef = ref(database, `games/${gameId}/teams/${teamId}`);
    const unsubscribe = onValue(teamRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setTeamData({ name: data.name, emoji: data.emoji });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [gameId, teamId]);

  if (!gameId || !teamId) return <div>Ruta inválida</div>;
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
          <div style={{ fontSize: 18, color: '#64748b' }}>Cargando...</div>
        </div>
      </div>
    );
  }
  if (!teamData) return <div>Equipo no encontrado</div>;

  return (
    <ProposalStudentView
      gameId={gameId}
      teamId={teamId}
      teamName={teamData.name}
      teamEmoji={teamData.emoji}
    />
  );
}

// ✅ NUEVO: Wrapper para SetupFlowManager
function SetupFlowManagerWrapper() {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Detectar si viene de biblioteca
  const fromLibrary = location.state?.fromLibrary === true;

  // ✅ Si viene de biblioteca, ir directo al SetupScreen tradicional
  useEffect(() => {
    if (fromLibrary) {
      console.log("🟢 Detected fromLibrary, redirecting to /setup-traditional");
      navigate('/setup-traditional', { state: { fromLibrary: true } });
    }
  }, [fromLibrary, navigate]);

  const handleGameCreated = (newGameId: string) => {
    console.log("Game created with ID:", newGameId);
    navigate(`/classroom/${newGameId}`);
  };

  // Si viene de biblioteca, mostrar loading mientras redirige
  if (fromLibrary) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="App">
      <div style={{ position: "fixed", top: 16, right: 16, zIndex: 1000 }}>
        <SoundToggle />
      </div>
      <SetupFlowManager onGameCreated={handleGameCreated} />
    </div>
  );
}

type AppView = "setup" | "game";

/**
 * ✅ App original del docente (setup/game) - Flujo tradicional
 * ⚠️ NOTA: Esta vista contiene elementos de debug que deberían ocultarse en producción
 */
function TeacherAppLegacy() {
  const [currentView, setCurrentView] = useState<AppView>("setup");
  const [gameId, setGameId] = useState<string | null>(null);
  const [teamId, setTeamId] = useState("teamA");
  const [availableTeams, setAvailableTeams] = useState<string[]>([]);

  // ✅ Detectar si estamos en producción
  const isProduction = import.meta.env.PROD;

  useEffect(() => {
    if (!gameId) return;

    const teamsRef = ref(database, `games/${gameId}/teams`);
    const unsubscribe = onValue(teamsRef, (snapshot) => {
      const teams = snapshot.val();
      if (teams) {
        const teamIds = Object.keys(teams);
        setAvailableTeams(teamIds);

        if (teamIds.length > 0 && !teamIds.includes(teamId)) {
          setTeamId(teamIds[0]);
        }
      }
    });

    return () => unsubscribe();
  }, [gameId, teamId]);

  const handleGameCreated = (newGameId: string) => {
    console.log("Game created with ID:", newGameId);
    setGameId(newGameId);
  };

  const handleStartGame = () => {
    if (gameId) setCurrentView("game");
  };

  // SETUP VIEW
  if (currentView === "setup") {
    return (
      <div className="App">
        <div style={{ position: "fixed", top: 16, right: 16, zIndex: 1000 }}>
          <SoundToggle />
        </div>

        <SetupScreen onGameCreated={handleGameCreated} />

        {/* ⚠️ Botón de testing - solo en desarrollo */}
        {gameId && !isProduction && (
          <div
            style={{
              position: "fixed",
              bottom: "20px",
              right: "20px",
              background: "#27ae60",
              padding: "15px 30px",
              borderRadius: "8px",
              cursor: "pointer",
              color: "white",
              fontWeight: "bold",
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            }}
            onClick={handleStartGame}
          >
            🎮 IR AL JUEGO (Testing)
          </div>
        )}
      </div>
    );
  }

  // GAME VIEW
  if (currentView === "game" && gameId) {
    return (
      <div className="App">
        <div style={{ position: "fixed", top: 16, right: 16, zIndex: 1000 }}>
          <SoundToggle />
        </div>

        {/* ⚠️ Consola del profesor - solo en desarrollo */}
        {!isProduction && (
          <div
            style={{
              position: "fixed",
              top: "20px",
              left: "20px",
              zIndex: 10000,
              background: "white",
              padding: "12px 16px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div style={{ fontSize: "12px", fontWeight: "bold", color: "#666" }}>
              🎮 CONSOLA DEL PROFESOR (DEV)
            </div>

            <select
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              style={{
                padding: "8px 12px",
                fontSize: "14px",
                borderRadius: "6px",
                border: "2px solid #3498db",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              {availableTeams.map((tId) => (
                <option key={tId} value={tId}>
                  {tId === "teamA" && "🔴 Team A"}
                  {tId === "teamB" && "🟢 Team B"}
                  {tId === "teamC" && "🔵 Team C"}
                  {tId === "teamD" && "🟡 Team D"}
                  {tId === "teamE" && "🟣 Team E"}
                  {tId === "teamF" && "🟠 Team F"}
                  {!["teamA", "teamB", "teamC", "teamD", "teamE", "teamF"].includes(tId) && `📦 ${tId}`}
                </option>
              ))}
            </select>

            <div style={{ fontSize: "11px", color: "#999" }}>
              Equipo actual: <strong>{teamId}</strong>
            </div>
          </div>
        )}

        <GameController key={teamId} gameId={gameId} teamId={teamId} />

        {/* ⚠️ Botón volver - solo en desarrollo */}
        {!isProduction && (
          <button
            style={{
              position: "fixed",
              bottom: "20px",
              left: "20px",
              background: "#95a5a6",
              color: "white",
              padding: "10px 14px",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "12px",
              border: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              zIndex: 9999,
            }}
            onClick={() => setCurrentView("setup")}
          >
            ← Volver a Setup
          </button>
        )}
      </div>
    );
  }

  return null;
}

// 🆕 Componente de carga para lazy loading
function LoadingSpinner() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh'
    }}>
      <p>Cargando...</p>
    </div>
  );
}

// 🆕 Actualiza el <link rel="manifest"> según la ruta (árbitro vs juego normal)
function ManifestManager() {
  const location = useLocation();
  useEffect(() => {
    setManifestForRoute();
  }, [location.pathname, location.search]);
  return null;
}

// 🆕 Fija <html lang> en las rutas del árbitro (/arbitro*, /sesion/*) sin mirar el
// idioma del navegador: "es", o el de la sesión en las pantallas de los alumnos.
// En el resto sigue el idioma elegido.
function ArbitroLangManager() {
  const location = useLocation();
  const { language, sessionLanguage } = useI18n();
  useEffect(() => {
    document.documentElement.lang = htmlLangForPath(location.pathname, language, sessionLanguage);
  }, [location.pathname, language, sessionLanguage]);
  return null;
}

// 🆕 /arbitro-juego/:sessionId: si el código en la URL trae minúsculas, espacios o guiones
// de más, redirige a la forma canónica ANTES de montar ArbitroJuegoPage. No hace esta
// comprobación como un return temprano dentro de ArbitroJuegoPage: al cambiar solo el
// parámetro de la URL, React reutiliza la misma instancia del componente, y si el primer
// render no llegó a llamar a todos sus hooks (por el return temprano) y el segundo sí,
// rompe las reglas de hooks. Este wrapper, en cambio, llama siempre el mismo único hook
// (useParams) y decide ANTES de montar ArbitroJuegoPage, que así nace ya con el código bueno.
function ArbitroJuegoRutaCanonica() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const canonico = sessionId ? normalizarCodigoSesion(sessionId) : sessionId;
  if (sessionId && canonico !== sessionId) {
    return <Navigate to={`/arbitro-juego/${canonico}`} replace />;
  }
  return <ArbitroJuegoPage />;
}

function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <GameModeProvider>
          <BrowserRouter>
            <>
              <ManifestManager />
              <ArbitroLangManager />
              <PWAMiniBadge />
              <PWAInstallBanner />
              <Routes>
                {/* Login */}
                <Route path="/login" element={<LoginPage />} />

                {/* Política de privacidad */}
                <Route path="/privacy" element={<PrivacyPage />} />

                {/* PÁGINA PRINCIPAL: pública, entrada a las tres propuestas */}
                <Route path="/" element={<HomePage />} />

                {/* SEMÁFORO: todo lo que antes vivía en "/" y es solo del Semáforo (con sesión) */}
                <Route
                  path="/semaforo"
                  element={
                    <ProtectedRoute>
                      <SemaforoPage />
                    </ProtectedRoute>
                  }
                />

                {/* HERRAMIENTAS: pública, todas las tarjetas a la vista */}
                <Route path="/herramientas" element={<HerramientasPage />} />

                {/* /dashboard ya no existe: por los que la tengan guardada (bookmark, atajo viejo) */}
                <Route path="/dashboard" element={<Navigate to="/semaforo" replace />} />

                {/* ABOUT / FAQ: pública, no muestra datos del usuario */}
                <Route path="/about" element={<AboutPage />} />

                {/* REFERRAL PAGE */}
                <Route path="/referir" element={<ReferralPage />} />

                {/* WEBINAR REGISTRATION PAGE */}
                <Route path="/webinar-registro" element={<WebinarRegistrationPage />} />

                {/* 🆕 GENERADOR DE RÚBRICAS */}
                <Route
                  path="/rubric-generator"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <RubricGenerator />
                    </Suspense>
                  }
                />

                {/* 🆕 ADAPTADOR DE CONSIGNAS */}
                <Route
                  path="/consigna-adapter"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <ConsignaAdapter />
                    </Suspense>
                  }
                />

                {/* 🆕 GENERADOR DE INFORMES */}
                <Route
                  path="/informe-generator"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <InformeGenerator />
                    </Suspense>
                  }
                />

                {/* 🆕 GENERADOR DE PLANILLA DE PRESENTISMO */}
                <Route
                  path="/presentismo-generator"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <PresentismoGenerator />
                    </Suspense>
                  }
                />

                {/* 🆕 GENERADOR DE PRESENTACIONES */}
                <Route
                  path="/presentacion-generator"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <PresentacionGenerator />
                    </Suspense>
                  }
                />

                {/* 🆕 GENERADOR DE HORARIOS */}
                <Route
                  path="/horario-generator"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <HorarioGenerator />
                    </Suspense>
                  }
                />

                {/* 🆕 GENERADOR DE SECUENCIAS DIDÁCTICAS */}
                <Route
                  path="/secuencia-generator"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <SecuenciaGenerator />
                    </Suspense>
                  }
                />
                {/* ✗ DESACTIVADO: Corrector de Rúbricas
                <Route
                  path="/correccion"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <Correccion />
                    </Suspense>
                  }
                />
                */}

                {/* 🆕 GENERADOR DE PLANIFICACIONES */}
                <Route
                  path="/planificacion-generator"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <PlanificacionGeneratorPage />
                    </Suspense>
                  }
                />

                {/* 🆕 GUÍA PEDAGÓGICA DEL PROFESOR */}
                <Route
                  path="/teacher-guide"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <TeacherGuide />
                    </Suspense>
                  }
                />

                {/* ✅ COMPARADOR DE PAÍSES */}
                <Route
                  path="/comparador-paises"
                  element={
                    <Suspense fallback={<div>Cargando...</div>}>
                      <ComparadorPaisesPage />
                    </Suspense>
                  }
                />

                {/* 🆕 GENERADOR DE ECUACIONES */}
                <Route
                  path="/ecuaciones-generator"
                  element={
                    <Suspense fallback={<div>Cargando...</div>}>
                      <EcuacionesGeneratorPage />
                    </Suspense>
                  }
                />

                {/* 🆕 VIANDAS DE MIRE */}
                <Route
                  path="/viandas-mire"
                  element={
                    <Suspense fallback={<div>Cargando...</div>}>
                      <ViandasMirePage />
                    </Suspense>
                  }
                />

                {/* 🆕 TABLAS DE MULTIPLICAR — BINGO */}
                <Route
                  path="/tablas-multiplicar-nivel-uno"
                  element={
                    <Suspense fallback={<div>Cargando...</div>}>
                      <TablasBingoPage />
                    </Suspense>
                  }
                />
                <Route path="/bingo-sumas-restas-nivel-uno" element={<Suspense fallback={<div>Cargando...</div>}><BingoSumasRestasPage /></Suspense>} />
                <Route path="/bingo-sumas-nivel-dos"        element={<Suspense fallback={<div>Cargando...</div>}><BingoSumasPage /></Suspense>} />
                <Route path="/bingo-tablas-nivel-dos"       element={<Suspense fallback={<div>Cargando...</div>}><BingoTablasDosPage /></Suspense>} />
                <Route path="/bingo-tablas-nivel-tres"      element={<Suspense fallback={<div>Cargando...</div>}><BingoTablasTresPage /></Suspense>} />
                <Route path="/bingo-familiar"               element={<Suspense fallback={<div>Cargando...</div>}><BingoFamiliarPage /></Suspense>} />

                {/* 🆕 ÁRBITRO DE LENGUA Y MATEMÁTICA */}
                <Route
                  path="/arbitro-config"
                  element={
                    <Suspense fallback={<div>Cargando...</div>}>
                      <ArbitroConfigPage />
                    </Suspense>
                  }
                />
                {/* 🆕 ÁRBITRO: Landing por área (link limpio para mails) */}
                <Route
                  path="/arbitro/:area"
                  element={
                    <Suspense fallback={<div>Cargando...</div>}>
                      <ArbitroLandingPage />
                    </Suspense>
                  }
                />
                {/* 🆕 ÁRBITRO: Portada pública (sin sesión), entrada para la postulación */}
                <Route
                  path="/arbitros"
                  element={
                    <Suspense fallback={<div>Cargando...</div>}>
                      <ArbitrosPublicaPage />
                    </Suspense>
                  }
                />
                {/* 🆕 ÁRBITRO: Página de información */}
                <Route
                  path="/arbitros-info"
                  element={
                    <Suspense fallback={<div>Cargando...</div>}>
                      <ArbitrosInfoPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/sesion/:sessionId"
                  element={
                    <Suspense fallback={<div>Cargando...</div>}>
                      <ArbitroSesionPage />
                    </Suspense>
                  }
                />
                {/* ⚔️ ÁRBITRO: Modo competitivo — 2 equipos con tablero de puntos */}
                <Route
                  path="/arbitro-juego/:sessionId"
                  element={
                    <Suspense fallback={<div>Cargando...</div>}>
                      <ArbitroJuegoRutaCanonica />
                    </Suspense>
                  }
                />
                {/* 🆕 ÁRBITRO: ingreso de alumnos — un campo con el código de sesión.
                    Misma página en los dos dominios y en las dos rutas; el idioma sale
                    del dominio (como el resto de las pantallas del árbitro). */}
                <Route
                  path="/alumnos"
                  element={
                    <Suspense fallback={<div>Cargando...</div>}>
                      <ArbitroIngresoPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/students"
                  element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <ArbitroIngresoPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/arbitro-panel"
                  element={
                    <Suspense fallback={<div>Cargando...</div>}>
                      <ArbitroPanelPage />
                    </Suspense>
                  }
                />

                {/* AUTOEVALUACIÓN - SIN LOGIN (alumnos) */}
                <Route path="/autoevaluacion/:gameId/:teamId" element={<SelfEvaluationPage />} />

                {/* 🔁 NUEVA RUTA PARA AUTOEVALUACIÓN DESDE QR (sin teamId) */}
                <Route path="/self-eval/:gameId" element={<SelfEvalPage />} />

                {/* 📋 NUEVA RUTA PARA EL PANEL DEL DOCENTE (ver autoevaluaciones) */}
                <Route path="/teacher/evaluations/:gameId" element={<TeacherEvaluationsPage />} />

                {/* RESULTADOS DEL JUEGO - CON LOGIN (docente) */}
                <Route
                  path="/resultados/:gameId"
                  element={
                    <ProtectedRoute>
                      <GameResultsPage />
                    </ProtectedRoute>
                  }
                />

                {/* BIBLIOTECA */}
                <Route
                  path="/library"
                  element={
                    <ProtectedRoute>
                      <LibraryPage />
                    </ProtectedRoute>
                  }
                />

                {/* ADMIN: métricas */}
                <Route
                  path="/admin/metrics"
                  element={
                    <AdminRoute>
                      <AdminMetricsPage />
                    </AdminRoute>
                  }
                />

                {/* ADMIN: subir CSVs */}
                <Route
                  path="/admin/library/upload"
                  element={
                    <AdminRoute>
                      <AdminLibraryUploadPage />
                    </AdminRoute>
                  }
                />

                {/* ADMIN: bulk upload */}
                <Route
                  path="/admin/library/bulk"
                  element={
                    <AdminRoute>
                      <AdminBulkUploadPage />
                    </AdminRoute>
                  }
                />

                {/* ✅ ADMIN: corregir biblioteca */}
                <Route
                  path="/admin/library/fix"
                  element={
                    <AdminRoute>
                      <AdminLibraryFixPage />
                    </AdminRoute>
                  }
                />

                {/* ✅ DOCENTE: Setup - Selector de modalidad (NUEVO) */}
                <Route
                  path="/setup"
                  element={
                    <ProtectedRoute>
                      <SetupFlowManagerWrapper />
                    </ProtectedRoute>
                  }
                />

                {/* ✅ DOCENTE: Setup tradicional (profesor crea las preguntas) */}
                <Route
                  path="/setup-traditional"
                  element={
                    <ProtectedRoute>
                      <TeacherAppLegacy />
                    </ProtectedRoute>
                  }
                />

                {/* DOCENTE: Classroom Stage 1 */}
                <Route
                  path="/classroom/:gameId"
                  element={
                    <ProtectedRoute>
                      <ClassroomRouteWrapper />
                    </ProtectedRoute>
                  }
                />

                {/* DOCENTE: Stage 2 Classroom */}
                <Route
                  path="/stage2/classroom/:gameId"
                  element={
                    <ProtectedRoute>
                      <Stage2ClassroomRouteWrapper />
                    </ProtectedRoute>
                  }
                />

                {/* ALUMNOS / EQUIPOS: SIN LOGIN */}
                <Route path="/join" element={<JoinGamePage />} />

                {/* ✅ ALUMNOS: Vista de propuestas (NUEVO) */}
                <Route path="/propose/:gameId/:teamId" element={<ProposalRouteWrapper />} />

                <Route path="/team/:gameId/:teamId" element={<TeamRouteWrapper />} />
                <Route path="/stage2/team/:gameId/:teamId" element={<TeamRouteWrapper />} />

                {/* 📊 A/B TEST DASHBOARD — campaña arbitro_2026 */}
                <Route path="/abtest" element={<ABTestDashboard />} />

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </>
          </BrowserRouter>
        </GameModeProvider>
      </I18nProvider>
    </AuthProvider>
  );
}

export default App;